import { useFetch } from '@chipinside/fetcher'
import { Grid, Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Avatar, Button, ListItemText, Tooltip } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Paper from '@mui/material/Paper'
import MuiSkeleton from '@mui/material/Skeleton'
import { styled } from '@mui/material/styles'
import { isEmpty, isNil } from 'lodash'
import React from 'react'
import { useHistory } from 'react-router-dom'

import { Dialog } from '#/components/Dialog'
import DrawerBox from '#/components/DrawerBox'
import FindMyCow, { FindMyCowButton } from '#/components/FindMyCow'
import Flex from '#/components/Flex'
import FormatCell from '#/components/FormatCell'
import { Icon } from '#/components/Icon'
import { useFarmSlug } from '#/store/ducks/farm'
import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import Colors from '#/styles/Old/Colors'
import stringLimit from '#/utils/stringLimit'

const styles = {
  avatar: {
    backgroundColor: Colors.heat_alert,
    width: '3em',
    height: '3em',
    marginRight: 20,
  },
  detected: {
    fontSize: 11,
    fontWeight: 300,
    color: Colors.darkgrey,
    display: 'block',
    lineHeight: '11px',
  },
  drawer: {
    borderTop: '1px solid #e0e0e0',
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header_status: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginRight: '10px',
  },
}

const StyledPaper = styled(Paper)(({ type, padding = 10 }) => ({
  ...(type === 'info' && {
    background: '#fafafa',
  }),
  borderRadius: 8,
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  margin: 5,
  padding,
}))
const MatingStyle = styled(ListItem)({
  background: '#fafafa',
  marginBottom: '8px',
  padding: '5px 16px',
  borderRadius: 8,
})
const FooterWrapper = styled(Box)({
  marginTop: 'auto',
  display: 'flex',
  justifyContent: 'space-around',
  padding: '10px 0',
  width: '100%',
})

const HealthWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
})

const EmptyList = styled('small')({
  textAlign: 'center',
  display: 'block',
  margin: '10px 0',
  color: 'gray',
})

const StyledListItem = styled(ListItem)({
  display: 'flex',
  alignItems: 'center',
  gap: 30,
})

const StyledList = styled(List)({
  overflowY: 'scroll',
  maxHeight: 300,
})

const InfosValue = styled('span')({
  fontSize: '20px',
  fontWeight: '700',
  color: '#666',
})

const InfosText = styled('p')({
  fontSize: '14px',
  fontWeight: '400',
  color: '#999',
  margin: 0,
})

const IntelLabel = styled('p')({
  fontSize: '14px',
  fontWeight: '400',
  color: '#999',
  margin: '0 0 10px',
})
const SessionTitle = styled('h4')({
  margin: '16px 0',
})

export default function AnimalDrawer(props) {
  const { controller, event = {}, open, onClose, afterSuccess } = props
  const { build } = useFormBuilder()
  const farmSlug = useFarmSlug()
  const history = useHistory()

  const animalSlug = event['subject.slug'] ?? event['animal.slug']

  const { data, isLoading } = useFetch({ controller })

  const {
    del,
    dui,
    lactations,
    services,
    heat_strength,
    dtr,
    ppi_status,
    health_status,
    diagnostics,
    confirmed_as,
    date,
    mating,
    voluntary_waiting_period_heats,
  } = data ?? {}

  const StatusBlocks = [
    {
      text: Translate({ messageKey: 'strength' }),
      value: <FormatCell type="heat-strength" value={heat_strength} />,
    },
    {
      text: Translate({ messageKey: 'DTR_initials' }),
      value: <FormatCell type="dtr" alignItems="center" value={dtr} flexDirection="column" />,
    },
    {
      text: Translate({ messageKey: 'ppi' }),
      value: <FormatCell type="ppi_status" value={ppi_status} />,
    },
  ]

  const ServicesBlocks = [
    {
      text: Translate({ messageKey: 'DEL' }),
      label: Translate({ messageKey: 'DEL_long_text' }),
      value: del,
    },
    {
      text: Translate({ messageKey: 'DUI' }),
      label: Translate({ messageKey: 'DUI_long_text' }),
      value: dui,
    },
    {
      text: Translate({ messageKey: 'lactation_plural' }),
      label: Translate({ messageKey: 'lactation_plural' }),
      value: lactations,
    },
    {
      text: Translate({ messageKey: 'service_plural' }),
      label: Translate({ messageKey: 'service_plural' }),
      value: services,
    },
    {
      text: Translate({ messageKey: 'voluntary_waiting_period_heats' }),
      label: Translate({ messageKey: 'voluntary_waiting_period_heats' }),
      value: voluntary_waiting_period_heats,
    },
  ]

  const DrawerHeader = props => {
    const { name, earring, date, reproduction_status, inapt, loading, batch } = props
    const Skeleton = value =>
      loading ? <MuiSkeleton width={`${parseInt(50 + Math.random() * 50, 10)}%`} /> : value

    return (
      <Flex>
        <Avatar style={styles.avatar} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <Icon size="large" name="reproduction" style={styles.icon} />
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Skeleton(
            <div style={styles.header_status}>
              <h3 className="m0">{stringLimit(`${earring} - ${batch?.name}`, 20)}</h3>
              <FindMyCowButton onClick={() => handleConfirm({ type: 'find_my_cow' })} />
            </div>,
          )}
          <div>
            <p className="m0">{Skeleton(stringLimit(name, 37))}</p>
            <small style={styles.detected}>
              {Skeleton(
                Translate({
                  messageKey: 'detected',
                  params: { item: Moment(date).fromNow() },
                }),
              )}
            </small>
          </div>
          {Skeleton(
            <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
              <FormatCell type="reproduction_status" value={reproduction_status} />
              <FormatCell type="inapt" flag={inapt} />
            </Box>,
          )}
        </Box>
      </Flex>
    )
  }

  const handleConfirm = ({ type, slug }) => {
    switch (type) {
      case 'reproduction_events_heat_confirm':
        build({
          formName: 'reproduction_events',
          formType: 'heat_confirm',
          params: {
            farm: farmSlug,
            eventSlug: slug,
            animalEarring: event['animal.earring'],
            animalName: event['animal.name'],
          },
        })
        break

      case 'find_my_cow':
        Dialog.open({
          maxWidth: 'md',
          content: <FindMyCow farm={farmSlug} animal={animalSlug} type="animal" />,
        })
        break

      default:
        break
    }
  }

  const IA = () => {
    return (
      <Grid size={12}>
        <SessionTitle>{Translate({ messageKey: 'best_time_for_ai' })}</SessionTitle>
        <FormatCell type="ai-timeline" size="large" value={data?.ai} />
      </Grid>
    )
  }

  const Intel = () => {
    return (
      <Grid size={12}>
        <SessionTitle>{Translate({ messageKey: 'intelligence_parameters' })}</SessionTitle>
        <Grid container>
          {StatusBlocks.map(({ value, text }, index) => {
            return (
              <React.Fragment key={text}>
                <Grid
                  size={{ xs: 6, sm: 4 }}
                  divider={index < StatusBlocks.length - 1 ? 'right' : null}
                >
                  <StyledPaper padding={0}>
                    <IntelLabel>{text}</IntelLabel>
                    {value}
                  </StyledPaper>
                </Grid>
              </React.Fragment>
            )
          })}
        </Grid>
      </Grid>
    )
  }

  const AdditionalInfos = () => {
    return (
      <Grid size={12}>
        <SessionTitle>{Translate({ messageKey: 'additional_info' })}</SessionTitle>
        <Grid container>
          {ServicesBlocks.map(({ value, text, label }) => {
            return (
              <Grid size={{ xs: 6, sm: 4 }} key={text}>
                <Tooltip title={label}>
                  <StyledPaper type="info">
                    <InfosValue>{value ?? '-'}</InfosValue>
                    <InfosText>{text}</InfosText>
                  </StyledPaper>
                </Tooltip>
              </Grid>
            )
          })}
        </Grid>
      </Grid>
    )
  }

  const Health = () => {
    return (
      <Grid size={12}>
        <HealthWrapper>
          <SessionTitle>{Translate({ messageKey: 'health' })}</SessionTitle>
          <FormatCell type="health_status" value={health_status} />
        </HealthWrapper>
        {diagnostics?.length ? (
          <StyledList>
            {diagnostics?.map((diagnostic, index) => (
              <React.Fragment key={`health-list-${index}`}>
                <StyledListItem>
                  <FormatCell type="date" value={diagnostic.date} />
                  <span>{diagnostic.text}</span>
                </StyledListItem>
                <Divider />
              </React.Fragment>
            ))}
          </StyledList>
        ) : (
          <EmptyList>
            {Translate({
              messageKey: 'without_diagnostic_text',
              params: { item: '30' },
            })}
          </EmptyList>
        )}
      </Grid>
    )
  }

  const Mating = () => {
    return (
      <Grid size={12}>
        <SessionTitle>{Translate({ messageKey: 'best_bull_mating' })}</SessionTitle>
        <List disablePadding>
          {isEmpty(mating) ? (
            <MatingStyle>
              <ListItemText primary={Translate({ messageKey: 'without_information' })} />
            </MatingStyle>
          ) : (
            mating?.map((item, index) => {
              return (
                <MatingStyle key={item}>
                  <ListItemText primary={`${index + 1} - ${item}`} />
                </MatingStyle>
              )
            })
          )}
        </List>
      </Grid>
    )
  }

  const Footer = () => {
    return (
      <FooterWrapper>
        {animalSlug ? (
          <Button
            variant="outlined"
            onClick={() => {
              if (afterSuccess) {
                afterSuccess()
              }
              history.push(`/farm/${farmSlug}/modules/herd/animals/${animalSlug}`)
            }}
          >
            {Translate({
              messageKey: ['access_item', 'animal'],
              lower: [false, true],
            })}
          </Button>
        ) : null}
        {Moment.diff('now', date, 'h') < 72 && isNil(confirmed_as) ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              handleConfirm({
                type: 'reproduction_events_heat_confirm',
                slug: event?.slug,
              })
            }
          >
            {Translate({ messageKey: 'confirm_item' })}
          </Button>
        ) : null}
      </FooterWrapper>
    )
  }

  return (
    <DrawerBox
      header={<DrawerHeader {...data} loading={isLoading} />}
      isOpen={open}
      onClose={onClose}
      contentStyle={styles.drawer}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid container spacing={1} rowSpacing={3}>
            <IA />
            <Mating />
            <Intel />
            <AdditionalInfos />
            <Health />
          </Grid>
          <Footer />
        </>
      )}
    </DrawerBox>
  )
}
